import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
  SimpleGrid,
  Stack,
  Flex,
  InputGroup,
} from "@chakra-ui/react";

import {
  Plus,
  Search,
} from "lucide-react";

import GradientButton from "components/Buttons/GradientButton";
import ReusableInput from "components/Inputs/ReusableInput";
import NoDataFound from "components/NoDataGraphics/NoDataFound";

import AddStarterKitComponentDrawer from "./components/AddStarterKitComponent";
import StarterKitComponentCard from "./components/StarterKitComponentCard";
import EditStarterKitComponentDrawer from "./components/EditStarterKitComponent";
import DeleteStarterKitComponent from "./components/DeleteStarterKitComponent";

import { useDispatch, useSelector } from "react-redux";

import {
  fetchStarterKitComponentsAction,
  createStarterKitComponentAction,
  updateStarterKitComponentAction,
  deleteStarterKitComponentAction,
} from "state/actions/starterKitComponentActions";


const StarterKitComponentsTab = () => {
  const dispatch = useDispatch();

  const {
    starterKitComponents,
    loading,
    error,
  } = useSelector(
    (state) => state.starterKitComponents
  );

  const [searchTerm, setSearchTerm] = useState("");

  const [
    isAddStarterKitComponentDrawerOpen,
    setAddStarterKitComponentDrawerOpen,
  ] = useState(false);

  const [
    isEditStarterKitComponentDrawerOpen,
    setEditStarterKitComponentDrawerOpen,
  ] = useState(false);

  const [
    selectedStarterKitComponent,
    setSelectedStarterKitComponent,
  ] = useState(null);

  const [
    isDeleteModalOpen,
    setDeleteModalOpen,
  ] = useState(false);

  const [
    selectedComponent,
    setSelectedComponent,
  ] = useState(null);


  useEffect(() => {
    dispatch(fetchStarterKitComponentsAction());
  }, [dispatch]);


  const handleOpenAddStarterKitComponentDrawer = () => {
    setAddStarterKitComponentDrawerOpen(true);
  };


  const handleCloseAddStarterKitComponentDrawer = () => {
    setAddStarterKitComponentDrawerOpen(false);
  };


  const handleAddStarterKitComponent = async (newComponent) => {
    const response = await dispatch(
      createStarterKitComponentAction(
        newComponent
      )
    );

    if (response?.success) {
      handleCloseAddStarterKitComponentDrawer();

      dispatch(
        fetchStarterKitComponentsAction()
      );
    }

    return response;
  };


  const handleOpenEditStarterKitComponentDrawer = (
    component
  ) => {
    setSelectedStarterKitComponent(component);

    setEditStarterKitComponentDrawerOpen(
      true
    );
  };


  const handleCloseEditStarterKitComponentDrawer = () => {
    setEditStarterKitComponentDrawerOpen(false);
  };


  const handleUpdateStarterKitComponent = async (
    componentId,
    formData
  ) => {
    const response = await dispatch(
      updateStarterKitComponentAction(
        componentId,
        formData
      )
    );

    if (response?.success) {
      handleCloseEditStarterKitComponentDrawer();

      dispatch(
        fetchStarterKitComponentsAction()
      );
    }

    return response;
  };


  const handleDeleteStarterKitComponent = (
    component
  ) => {
    setSelectedComponent(component);

    setDeleteModalOpen(true);
  };


  const confirmDeleteStarterKitComponent = async () => {
    if (!selectedComponent) return;

    const response = await dispatch(
      deleteStarterKitComponentAction(
        selectedComponent.starter_kit_component_id
      )
    );

    if (response?.success) {
      setDeleteModalOpen(false);

      setSelectedComponent(null);

      dispatch(
        fetchStarterKitComponentsAction()
      );
    }
  };


  const filteredStarterKitComponents =
    (starterKitComponents || []).filter(
      (component) => {

        const search =
          searchTerm.toLowerCase();

        return (
          component.component_name
            ?.toLowerCase()
            .includes(search) ||

          component.component_code
            ?.toLowerCase()
            .includes(search) ||

          component.description
            ?.toLowerCase()
            .includes(search)
        );
      }
    );


  return (
    <Stack spacing={5}>

      <Flex
        justifyContent="space-between"
        alignItems="center"
      >

        {loading ? (
          <Skeleton
            width="300px"
            height="40px"
            borderRadius="12px"
            ml={5}
          />
        ) : (
          <InputGroup
            width="300px"
            ml={5}
            borderRadius="12px"
          >
            <ReusableInput
              icon={
                <Search
                  size={20}
                  color="#A0AEC0"
                />
              }
              placeholder="Search starter kit components..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(
                  e.target.value
                )
              }
            />
          </InputGroup>
        )}


        {loading ? (
          <Skeleton
            width="180px"
            height="40px"
            borderRadius="12px"
            mr={5}
          />
        ) : (
          <GradientButton
            icon={<Plus size={16} />}
            onClick={
              handleOpenAddStarterKitComponentDrawer
            }
            height="40px"
            mr={5}
          >
            Add Component
          </GradientButton>
        )}

      </Flex>


      <SimpleGrid
        columns={{
          base: 1,
          md: 2,
          lg: 3,
          xl: 4,
        }}
        spacing={5}
        p={5}
        mt={20}
      >

        {filteredStarterKitComponents.map(
          (component) => (
            <StarterKitComponentCard
              key={
                component.starter_kit_component_id
              }
              {...component}
              onEdit={() =>
                handleOpenEditStarterKitComponentDrawer(
                  component
                )
              }
              onDelete={() =>
                handleDeleteStarterKitComponent(
                  component
                )
              }
              loading={loading}
            />
          )
        )}

      </SimpleGrid>


      {!loading &&
        filteredStarterKitComponents.length ===
          0 && (
          <Flex
            justifyContent="center"
            alignItems="center"
            height="300px"
            width="100%"
          >
            <NoDataFound
              message={
                searchTerm
                  ? "No starter kit components found matching your search."
                  : "No starter kit components found."
              }
            />
          </Flex>
        )}


      <AddStarterKitComponentDrawer
        isOpen={
          isAddStarterKitComponentDrawerOpen
        }
        onClose={
          handleCloseAddStarterKitComponentDrawer
        }
        onAddStarterKitComponent={
          handleAddStarterKitComponent
        }
      />


      {selectedStarterKitComponent && (
        <EditStarterKitComponentDrawer
          isOpen={
            isEditStarterKitComponentDrawerOpen
          }
          onClose={
            handleCloseEditStarterKitComponentDrawer
          }
          starterKitComponent={
            selectedStarterKitComponent
          }
          onUpdateStarterKitComponent={
            handleUpdateStarterKitComponent
          }
        />
      )}


      <DeleteStarterKitComponent
        isOpen={isDeleteModalOpen}
        onClose={() =>
          setDeleteModalOpen(false)
        }
        onConfirm={
          confirmDeleteStarterKitComponent
        }
        componentName={
          selectedComponent
            ? selectedComponent.component_name
            : ""
        }
      />

    </Stack>
  );
};


export default StarterKitComponentsTab;